import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";

const CORRECT_PASSWORD = "EvolveTW";
const ADMIN_STORAGE_KEY = "evolve_admin_auth";
const USER_STORAGE_KEY = "evolve_user_auth";

export default function PasswordGate({ children, type = "admin" }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const storageKey = type === "admin" ? ADMIN_STORAGE_KEY : USER_STORAGE_KEY;

  useEffect(() => {
    // Check if already authenticated
    const auth = sessionStorage.getItem(storageKey);
    if (auth === "true") {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, [storageKey]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      sessionStorage.setItem(storageKey, "true");
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white/10 backdrop-blur-xl">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EVOLVE
            </h1>
            <p className="text-slate-400">
              {type === "admin" ? "Admin Portal Access" : "User Portal Access"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pl-10 pr-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-500 rounded-xl"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="login-btn"
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
            >
              Access Portal
            </Button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-6">
            Secure access for authorized personnel only
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
